/**
 * 🚀 Data-Driven Vehicle System
 *
 * This system is designed for high-performance rendering of a large number of vehicles
 * using a single InstancedMesh per vehicle type. It is data-driven, meaning it
 * updates vehicle positions and rotations based on incoming data streams (e.g., RSM).
 *
 * It is self-contained and handles its own model loading based on configuration.
 *
 * Features:
 * - High-performance rendering with THREE.InstancedMesh.
 * - Configurable models per participant type (ptcType).
 * - Automatic fallback to colored primitive shapes for unconfigured types.
 * - Smooth animation using temporal interpolation with easing.
 * - Efficiently handles vehicle creation, updates, and removal.
 * - Built-in click detection to identify individual vehicles.
 */

import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import * as BufferGeometryUtils from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { headingToQuaternion, cubicHermiteSpline } from '../utils/math';
import { easeOutQuart } from '../utils/eases';
import type { CoordinateSystem } from './CoordinateSystem';
import type { VehicleData } from './RSMDataAdapter';
import { RSMDataAdapter } from './RSMDataAdapter';

// 物理状态，用于精确插值
interface VehiclePhysicalState {
  position: THREE.Vector3;
  quaternion: THREE.Quaternion;
  velocity: THREE.Vector3;
  timestamp: number; // 原始RSM消息时间戳
  ptcType: number; // 💥 新增：必须携带粒子类型
}

// 💥 恢复为更简单、健壮的动画状态机
interface VehicleAnimationState {
  lastState: VehiclePhysicalState;
  targetState: VehiclePhysicalState;
  animationStartTime: number;
  animationDuration: number;
}

// --- New Configuration Interfaces ---

/**
 * 模型配置
 */
export interface VehicleModelConfig {
  url: string; // 模型URL
  /**
   * 💥 [性能关键] 是否用模型的包围盒替代原始模型进行渲染。
   * 对于高精度模型，强烈建议开启此项以保证性能。
   * @default true
   */
  useBoundingBox?: boolean;
}

/**
 * DataDrivenVehicleSystem 的配置选项
 */
export interface DataDrivenVehicleSystemOptions {
  /** 每种参与者类型的最大实例数 */
  maxInstancesPerType?: number;
  /** 定义 ptcType 到模型的映射 */
  models?: Map<number, VehicleModelConfig | string>;
  /** Draco解码器路径 */
  dracoDecoderPath?: string;
  /**
   * 缓冲区处理间隔（毫秒）。
   * 系统处理累积数据并启动新动画的频率。
   * 🚀 10Hz RSM适配：推荐100ms间隔
   * 🔧 1Hz RSM：使用1000ms间隔
   * @default 100
   */
  processingInterval?: number;
  /**
   * 🚀 启用数据平滑处理，用于高频数据（如10Hz）
   * @default true
   */
  enableDataSmoothing?: boolean;
  /**
   * 🎯 历史数据缓存大小，用于数据平滑
   * @default 3
   */
  historyCacheSize?: number;
}

// 为不同参与者类型定义颜色和名称
const PtcTypeConfig: Record<number, { color: THREE.Color; name: string }> = {
  0: { color: new THREE.Color(0xffffff), name: '未知设备' },
  1: { color: new THREE.Color(0x0099ff), name: '机动车' },
  2: { color: new THREE.Color(0x00ff99), name: '非机动车' },
  3: { color: new THREE.Color(0xff9900), name: '行人' },
  4: { color: new THREE.Color(0xff6b6b), name: 'RSU设备' },
  5: { color: new THREE.Color(0xcccccc), name: '保留类型' },
};

export class DataDrivenVehicleSystem {
  private scene: THREE.Scene;
  private camera: THREE.Camera;
  private domElement: HTMLElement;
  private coordinateSystem: CoordinateSystem;

  // --- Multi-InstancedMesh Architecture ---
  private instancedMeshes: Map<number, THREE.InstancedMesh> = new Map();
  // 💥 关键的状态机，存储所有需要播放动画的车辆
  private activeAnimations: Map<string, VehicleAnimationState> = new Map();
  private vehicleDataMap: Map<string, VehicleData> = new Map();
  private vehicleIdToIndex: Map<number, Map<string, number>> = new Map();
  private availableIndices: Map<number, number[]> = new Map();
  private modelGeometries: Map<number, THREE.BufferGeometry> = new Map();
  private modelMaterials: Map<number, THREE.Material | THREE.Material[]> =
    new Map();

  private maxInstancesPerType: number;
  private animationFrameId: number | null = null;
  private raycaster: THREE.Raycaster;
  private onVehicleClick?: (
    vehicleId: string,
    vehicleData: VehicleData
  ) => void;

  private loader: GLTFLoader;
  private modelConfigs: Map<number, VehicleModelConfig | string>;
  private isInitialized = false;

  // 🚀 数据缓冲区和处理定时器
  private messageBuffer: VehicleData[][] = [];
  private processingIntervalHandle: ReturnType<typeof setInterval> | null =
    null;
  private processingInterval: number;

  // 🚀 10Hz适配：数据平滑处理
  private enableDataSmoothing: boolean;
  private historyCacheSize: number;
  private vehicleHistory: Map<string, VehicleData[]> = new Map();

  private stats = {
    vehicleCount: 0,
    created: 0,
    updated: 0,
  };
  private defaultScale = new THREE.Vector3(1, 1, 1);

  // --- New properties for ground detection ---
  private groundMeshes: THREE.Mesh[] = [];
  private groundRaycaster = new THREE.Raycaster();

  constructor(
    scene: THREE.Scene,
    camera: THREE.Camera,
    domElement: HTMLElement,
    coordinateSystem: CoordinateSystem,
    options: DataDrivenVehicleSystemOptions = {}
  ) {
    this.scene = scene;
    this.camera = camera;
    this.domElement = domElement;
    this.coordinateSystem = coordinateSystem;
    this.maxInstancesPerType = options.maxInstancesPerType || 500;
    this.modelConfigs = options.models || new Map();
    this.raycaster = new THREE.Raycaster();
    // 🚀 10Hz RSM适配：默认100ms处理间隔
    this.processingInterval = options.processingInterval ?? 100;
    this.enableDataSmoothing = options.enableDataSmoothing ?? true;
    this.historyCacheSize = options.historyCacheSize ?? 3;

    this.loader = new GLTFLoader();
    if (options.dracoDecoderPath) {
      const dracoLoader = new DRACOLoader();
      dracoLoader.setDecoderPath(options.dracoDecoderPath);
      this.loader.setDRACOLoader(dracoLoader);
    }
  }

  /**
   * 注册用于高度检测的地面/路面模型。
   * @param meshes - 一个包含所有地面网格的数组。
   */
  public setGroundMeshes(meshes: THREE.Mesh[]): void {
    this.groundMeshes = meshes;
    console.log(
      `[DataDrivenVehicleSystem] ${meshes.length} ground meshes registered for height detection.`
    );
  }

  public async initialize(): Promise<void> {
    if (this.isInitialized) return;
    console.log('[DataDrivenVehicleSystem] Initializing...');

    for (const [ptcType, config] of this.modelConfigs.entries()) {
      const url = typeof config === 'string' ? config : config.url;
      try {
        console.log(
          `[DataDrivenVehicleSystem] Loading model for ptcType ${ptcType} from ${url}`
        );
        const gltf = await this.loader.loadAsync(url);
        const useBoundingBox =
          typeof config === 'string' ? true : config.useBoundingBox !== false;

        const modelData = this._extractModelData(gltf, useBoundingBox);

        if (modelData) {
          this.modelGeometries.set(ptcType, modelData.geometry);
          this.modelMaterials.set(ptcType, modelData.material);
          console.log(
            `[DataDrivenVehicleSystem] Model for ptcType ${ptcType} loaded. ${
              useBoundingBox ? 'Using BoundingBox.' : 'Using full geometry.'
            }`
          );
        }
      } catch (e) {
        console.error(
          `[DataDrivenVehicleSystem] Failed to load model for ptcType ${ptcType} from ${url}`,
          e
        );
      }
    }

    this.start();
    // 🚀 启动缓冲区处理器
    this.startProcessingLoop();
    this.domElement.addEventListener('click', this.onCanvasClick.bind(this));
    this.isInitialized = true;
    console.log('[DataDrivenVehicleSystem] Initialization complete.');
  }

  private _extractModelData(
    gltf: any,
    useBoundingBox: boolean
  ): {
    geometry: THREE.BufferGeometry;
    material: THREE.Material | THREE.Material[];
  } | null {
    const geometries: THREE.BufferGeometry[] = [];
    let collectedMaterials: THREE.Material[] = [];
    const materialMap = new Map<string, THREE.Material>();

    gltf.scene.traverse((child: any) => {
      if (child.isMesh && child.geometry) {
        const clonedGeom = child.geometry.clone();
        clonedGeom.rotateX(-Math.PI / 2); // GLTF Z-up to Three.js Y-up
        clonedGeom.rotateY(-Math.PI); // GLTF Z-up to Three.js Y-up

        geometries.push(clonedGeom);

        if (child.material) {
          const materials = Array.isArray(child.material)
            ? child.material
            : [child.material];
          materials.forEach((mat: THREE.Material) => {
            if (!materialMap.has(mat.uuid)) {
              const matClone = mat.clone();
              if ((matClone as any).isMeshStandardMaterial) {
                (matClone as THREE.MeshStandardMaterial).envMapIntensity = 0.5;
              }
              materialMap.set(mat.uuid, matClone);
              collectedMaterials.push(matClone);
            }
          });

          // Remap material indices for the cloned geometry
          if (Array.isArray(child.material)) {
            clonedGeom.groups.forEach((group: { materialIndex?: number }) => {
              const originalMat = child.material[group.materialIndex!];
              if (originalMat) {
                const newIndex = collectedMaterials.findIndex(
                  m => m.uuid === originalMat.uuid
                );
                if (newIndex !== -1) {
                  group.materialIndex = newIndex;
                }
              }
            });
          } else {
            const newIndex = collectedMaterials.findIndex(
              m => m.uuid === child.material.uuid
            );
            if (newIndex !== -1) {
              clonedGeom.groups.forEach((group: { materialIndex?: number }) => {
                group.materialIndex = newIndex;
              });
            }
          }
        }
      }
    });

    if (geometries.length === 0) return null;

    const merged = BufferGeometryUtils.mergeGeometries(geometries, true);
    if (!merged) {
      console.error(
        '[DataDrivenVehicleSystem] Failed to merge geometries. This model will be skipped.'
      );
      geometries.forEach(g => g.dispose());
      collectedMaterials.forEach(m => m.dispose());
      return null;
    }
    merged.computeBoundingBox();
    const boundingBox = merged.boundingBox!;
    const size = boundingBox.getSize(new THREE.Vector3());
    const center = boundingBox.getCenter(new THREE.Vector3());

    let finalGeometry: THREE.BufferGeometry;
    let finalMaterial: THREE.Material | THREE.Material[];

    if (useBoundingBox) {
      finalGeometry = new THREE.BoxGeometry(
        size.x > 0 ? size.x : 1,
        size.y > 0 ? size.y : 1,
        size.z > 0 ? size.z : 1
      );
      finalMaterial = new THREE.MeshLambertMaterial({
        color:
          (collectedMaterials[0] as any)?.color ||
          PtcTypeConfig[1]?.color ||
          0x0099ff,
      });
    } else {
      // 🛡️ 关键修复：标准化模型，防止变形
      // 1. 将几何体底部中心对齐到原点
      merged.translate(-center.x, -boundingBox.min.y, -center.z);

      // 2. Normalize scale
      const maxDim = Math.max(size.x, size.y, size.z);
      if (maxDim > 0) {
        // 假设机动车的标准尺寸长度(Z轴)为4.5米
        const scale = 4.5 / (size.z || maxDim);
        merged.scale(scale, scale, scale);
      }

      finalGeometry = merged;
      finalMaterial = collectedMaterials;

      if (finalMaterial.length === 0) {
        finalMaterial = new THREE.MeshLambertMaterial({ color: 0xcccccc });
      } else if (finalMaterial.length === 1) {
        finalMaterial = finalMaterial[0];
      }
    }

    finalGeometry.computeBoundingSphere();

    // Clean up temporary geometries
    geometries.forEach(g => g.dispose());

    return { geometry: finalGeometry, material: finalMaterial };
  }

  private ensureMeshForPtcType(ptcType: number): THREE.InstancedMesh {
    if (this.instancedMeshes.has(ptcType)) {
      return this.instancedMeshes.get(ptcType)!;
    }

    let geometry: THREE.BufferGeometry;
    let material: THREE.Material | THREE.Material[];

    if (this.modelGeometries.has(ptcType)) {
      geometry = this.modelGeometries.get(ptcType)!;
      material = this.modelMaterials.get(ptcType)!;
    } else {
      // 🎯 关键修复：修正默认几何体的尺寸，确保Z轴为车头方向
      switch (ptcType) {
        case 1: // 机动车: width=1.8, height=1.6, length=4.5
          geometry = new THREE.BoxGeometry(1.8, 1.6, 4.5);
          break;
        case 2: // 非机动车: width=0.6, height=1.2, length=1.8
          geometry = new THREE.BoxGeometry(0.6, 1.2, 1.8);
          break;
        case 3: // 行人
          geometry = new THREE.CylinderGeometry(0.3, 0.3, 1.7, 8);
          break;
        case 4: // RSU设备
          geometry = new THREE.BoxGeometry(0.5, 3.0, 0.5);
          break;
        default:
          geometry = new THREE.BoxGeometry(1, 1, 1);
      }
      const config = PtcTypeConfig[ptcType] || PtcTypeConfig[0];
      material = new THREE.MeshLambertMaterial({ color: config.color });
    }

    const newMesh = new THREE.InstancedMesh(
      geometry,
      material,
      this.maxInstancesPerType
    );
    newMesh.name = `InstancedMesh_${PtcTypeConfig[ptcType]?.name}_${ptcType}`;
    newMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);

    // 👁️ 关键修复：防止因相机距离过近导致整体消失的问题
    newMesh.frustumCulled = false;

    // 🚀 关键修复：将初始渲染数量设为0，防止在原点出现"幽灵"模型
    newMesh.count = 0;

    this.scene.add(newMesh);

    this.instancedMeshes.set(ptcType, newMesh);
    // 💥 初始化新的 State Map
    this.activeAnimations.set(ptcType.toString(), {
      lastState: {
        position: new THREE.Vector3(),
        quaternion: new THREE.Quaternion(),
        velocity: new THREE.Vector3(),
        timestamp: 0,
        ptcType: ptcType,
      },
      targetState: {
        position: new THREE.Vector3(),
        quaternion: new THREE.Quaternion(),
        velocity: new THREE.Vector3(),
        timestamp: 0,
        ptcType: ptcType,
      },
      animationStartTime: 0,
      animationDuration: 0,
    });
    this.vehicleDataMap.set(ptcType.toString(), {} as VehicleData);
    this.vehicleIdToIndex.set(ptcType, new Map());
    this.availableIndices.set(
      ptcType,
      Array.from({ length: this.maxInstancesPerType }, (_, i) => i).reverse()
    );
    return newMesh;
  }

  /**
   * 💥 主要修改：processRSMData 现在只负责将数据放入缓冲区
   * @param vehicles - 从 WebSocket 接收到的车辆数据数组
   */
  public processRSMData(data: VehicleData[]): void {
    if (data && data.length > 0) {
      this.messageBuffer.push(data);
    }
  }

  /**
   * 🚀 新增：启动周期性处理缓冲区的循环
   */
  private startProcessingLoop(): void {
    if (this.processingIntervalHandle) return;
    // 每秒处理一次缓冲区
    this.processingIntervalHandle = setInterval(
      this._processBufferedData.bind(this),
      this.processingInterval
    );
  }

  /**
   * 🚀 新增：停止处理循环
   */
  private stopProcessingLoop(): void {
    if (this.processingIntervalHandle) {
      clearInterval(this.processingIntervalHandle);
      this.processingIntervalHandle = null;
    }
  }

  /**
   * 🚀 新增：实际处理数据并更新车辆状态的核心逻辑
   * 🎯 10Hz适配：优化处理逻辑和动画时长计算
   */
  private _processBufferedData(): void {
    if (this.messageBuffer.length === 0) return;

    // 🎯 动态计算动画时长，略长于处理间隔以确保平滑过渡
    const animationDuration = Math.max(this.processingInterval * 1.2, 120);

    // 从缓冲区获取当前批次的数据，并清空
    const vehiclesToProcess = this.messageBuffer.flat();
    this.messageBuffer = [];

    // 使用最新的数据更新车辆
    const latestVehicleData = new Map<string, VehicleData>();
    vehiclesToProcess.forEach(v => {
      const id = this.getVehicleId(v);
      if (id) {
        latestVehicleData.set(id, v);
      }
    });

    this.stats.created = 0;
    this.stats.updated = 0;
    const allKnownIds = new Set(this.activeAnimations.keys());
    const incomingIds = new Set<string>();

    latestVehicleData.forEach(vehicle => {
      const id = this.getVehicleId(vehicle)!;
      incomingIds.add(id);

      // 🚀 10Hz适配：应用数据平滑处理
      const processedVehicle = this.enableDataSmoothing
        ? this.smoothVehicleData(id, vehicle)
        : vehicle;

      const ptcType = processedVehicle.ptcType;
      this.ensureMeshForPtcType(ptcType);
      this.vehicleDataMap.set(id, processedVehicle);

      const { x, y, z } = this.coordinateSystem.geographicToWorld3D({
        latitude: processedVehicle.latitude,
        longitude: processedVehicle.longitude,
        altitude: processedVehicle.elevation || 0,
      });
      const targetPosition = new THREE.Vector3(x, y, z);
      this.updateVehicleHeight(targetPosition); // 在这里就计算好高度

      const targetQuaternion = headingToQuaternion(processedVehicle.heading);
      const speedKmh = processedVehicle.speed || 0;
      const speedMps = speedKmh / 3.6;
      const velocityDirection = new THREE.Vector3(0, 0, 1).applyQuaternion(
        targetQuaternion
      );
      const targetVelocity = velocityDirection.multiplyScalar(speedMps);

      const newState: VehiclePhysicalState = {
        position: targetPosition,
        quaternion: targetQuaternion,
        velocity: targetVelocity,
        timestamp: processedVehicle.ptcTimestamp,
        ptcType: processedVehicle.ptcType,
      };

      const existingState = this.activeAnimations.get(id);

      if (existingState) {
        // 更新车辆
        if (newState.timestamp > existingState.targetState.timestamp) {
          existingState.lastState = existingState.targetState;
          existingState.targetState = newState;
          existingState.animationStartTime = performance.now();
          existingState.animationDuration = animationDuration;
          this.stats.updated++;
        }
      } else {
        // 新增车辆
        let lastState: VehiclePhysicalState;

        // 🚀 关键修复1：智能入场
        if (newState.velocity.lengthSq() > 0.001) {
          const artificialLastPosition = new THREE.Vector3()
            .copy(newState.position)
            .addScaledVector(
              newState.velocity,
              -1.0 * (animationDuration / 1000.0)
            );
          lastState = { ...newState, position: artificialLastPosition };
        } else {
          lastState = newState;
        }

        this.addVehicle(id, ptcType, lastState, newState, animationDuration);
        this.stats.created++;
      }
    });

    // 清理不再出现的车辆
    allKnownIds.forEach(id => {
      if (!incomingIds.has(id)) {
        const ptcType = this.activeAnimations.get(id)?.targetState.ptcType;
        if (ptcType !== undefined) {
          this.removeVehicle(id, ptcType);
        }
      }
    });
  }

  private getVehicleId(vehicle: VehicleData): string | null {
    const { vehicleId } = RSMDataAdapter.getStableVehicleId(
      vehicle,
      vehicle.runtime?.rsuId
    );
    return vehicleId;
  }

  private addVehicle(
    id: string,
    ptcType: number,
    lastState: VehiclePhysicalState,
    targetState: VehiclePhysicalState,
    animationDuration: number
  ) {
    const indices = this.availableIndices.get(ptcType);
    if (!indices || indices.length === 0) {
      console.warn(`No instance available for ptcType ${ptcType}`);
      return;
    }
    const instanceIndex = indices.pop()!;
    this.vehicleIdToIndex.get(ptcType)!.set(id, instanceIndex);
    this.activeAnimations.set(id, {
      lastState,
      targetState,
      animationStartTime: performance.now(),
      animationDuration,
    });
    this.instancedMeshes.get(ptcType)!.count =
      this.vehicleIdToIndex.get(ptcType)!.size;
  }

  private removeVehicle(vehicleId: string, ptcType: number): void {
    const idMap = this.vehicleIdToIndex.get(ptcType);
    const instanceIndex = idMap?.get(vehicleId);

    if (idMap && instanceIndex !== undefined) {
      this.availableIndices.get(ptcType)!.push(instanceIndex);
      idMap.delete(vehicleId);

      // 将要移除的实例与最后一个有效实例交换，然后缩减count
      const mesh = this.instancedMeshes.get(ptcType)!;
      const lastVehicleId = Array.from(idMap.keys()).pop();
      if (lastVehicleId) {
        const lastInstanceIndex = idMap.get(lastVehicleId)!;
        const tempMatrix = new THREE.Matrix4();
        mesh.getMatrixAt(lastInstanceIndex, tempMatrix);
        mesh.setMatrixAt(instanceIndex, tempMatrix);
        idMap.set(lastVehicleId, instanceIndex);
      }
      mesh.count = idMap.size;
      mesh.instanceMatrix.needsUpdate = true;

      this.activeAnimations.delete(vehicleId);
      this.vehicleDataMap.delete(vehicleId);
    }
  }

  private updateVehicleHeight(position: THREE.Vector3): void {
    if (this.groundMeshes.length > 0) {
      this.groundRaycaster.set(
        new THREE.Vector3(position.x, 100, position.z),
        new THREE.Vector3(0, -1, 0)
      );
      const intersects = this.groundRaycaster.intersectObjects(
        this.groundMeshes,
        true
      );
      if (intersects.length > 0) {
        position.y = intersects[0].point.y;
      }
    }
  }

  public update(): void {
    if (!this.isInitialized) return;

    const now = performance.now();
    const tempMatrix = new THREE.Matrix4();
    const tempPosition = new THREE.Vector3();
    const tempQuaternion = new THREE.Quaternion();

    this.activeAnimations.forEach((animation, id) => {
      const ptcType = animation.targetState.ptcType;
      const instancedMesh = this.instancedMeshes.get(ptcType);
      const instanceIndex = this.vehicleIdToIndex.get(ptcType)?.get(id);

      if (!instancedMesh || instanceIndex === undefined) return;

      // 🚀 关键修复2：运动外推
      const elapsed = now - animation.animationStartTime;
      const progress = elapsed / animation.animationDuration;

      if (progress < 1.0) {
        // --- 正常插值动画 ---
        const easedProgress = easeOutQuart(progress);
        const p0_tangent = animation.lastState.velocity.clone();
        const p1_tangent = animation.targetState.velocity.clone();
        tempPosition.copy(
          cubicHermiteSpline(
            animation.lastState.position,
            p0_tangent,
            animation.targetState.position,
            p1_tangent,
            easedProgress
          )
        );
        tempQuaternion.slerpQuaternions(
          animation.lastState.quaternion,
          animation.targetState.quaternion,
          easedProgress
        );
      } else {
        // --- 动画结束，开始外推 ---
        const timeSinceEnd = (elapsed - animation.animationDuration) / 1000.0;
        tempPosition.addVectors(
          animation.targetState.position,
          animation.targetState.velocity.clone().multiplyScalar(timeSinceEnd)
        );
        tempQuaternion.copy(animation.targetState.quaternion);
      }

      this.updateVehicleHeight(tempPosition);

      tempMatrix.compose(tempPosition, tempQuaternion, this.defaultScale);
      instancedMesh.setMatrixAt(instanceIndex, tempMatrix);
      instancedMesh.instanceMatrix.needsUpdate = true;
    });

    this.stats.vehicleCount = this.getVehicleCount();
  }

  private start() {
    if (this.animationFrameId !== null) return;
    const animate = () => {
      this.update();
      this.animationFrameId = requestAnimationFrame(animate);
    };
    animate();
  }

  private stop() {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  public setOnVehicleClick(
    callback: (vehicleId: string, vehicleData: VehicleData) => void
  ): void {
    this.onVehicleClick = callback;
  }

  public getVehicleWorldPosition(vehicleId: string): THREE.Vector3 | null {
    const animation = this.activeAnimations.get(vehicleId);
    if (animation) {
      // 为简化起见，我们直接返回目标位置。
      // 这对于需要高精度位置的标签来说可能略有延迟，但避免了复杂的重复计算。
      const pos = animation.targetState.position.clone();
      // 确保返回的高度是正确的
      this.updateVehicleHeight(pos);
      return pos;
    }
    return null;
  }

  public getVehicleData(vehicleId: string): VehicleData | undefined {
    return this.vehicleDataMap.get(vehicleId);
  }

  public getStats() {
    return { ...this.stats };
  }

  public getVehicleCount() {
    return Array.from(this.activeAnimations.keys()).length;
  }

  public getLegendData() {
    return Array.from(this.instancedMeshes.keys()).map(ptcType => {
      const config = PtcTypeConfig[ptcType] || PtcTypeConfig[0];
      return {
        type: ptcType,
        name: config.name,
        color: `#${config.color.getHexString()}`,
      };
    });
  }

  private onCanvasClick(event: MouseEvent): void {
    if (!this.onVehicleClick) return;
    const rect = this.domElement.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    const y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    this.raycaster.setFromCamera(new THREE.Vector2(x, y), this.camera);
    const intersects = this.raycaster.intersectObjects(
      Array.from(this.instancedMeshes.values())
    );

    if (intersects.length > 0) {
      const intersect = intersects[0];
      const instanceId = intersect.instanceId;
      if (instanceId === undefined) return;

      let clickedVehicleId: string | undefined;
      let clickedPtcType: number | undefined;

      for (const [ptcType, mesh] of this.instancedMeshes.entries()) {
        if (mesh === intersect.object) {
          for (const [id, index] of this.vehicleIdToIndex
            .get(ptcType)!
            .entries()) {
            if (index === instanceId) {
              clickedVehicleId = id;
              clickedPtcType = ptcType;
              break;
            }
          }
        }
        if (clickedVehicleId) break;
      }

      if (clickedVehicleId && clickedPtcType !== undefined) {
        const vehicleData = this.getVehicleData(clickedVehicleId);
        if (vehicleData) this.onVehicleClick(clickedVehicleId, vehicleData);
      }
    }
  }

  /**
   * 🚀 10Hz适配：数据平滑处理方法
   * 使用加权平均对高频数据进行平滑，减少抖动
   */
  private smoothVehicleData(
    vehicleId: string,
    current: VehicleData
  ): VehicleData {
    if (!this.enableDataSmoothing) return current;

    const history = this.vehicleHistory.get(vehicleId) || [];

    // 如果历史数据不足，直接返回当前数据
    if (history.length < 2) {
      history.push(current);
      this.vehicleHistory.set(vehicleId, history);
      return current;
    }

    // 🎨 使用加权平均进行平滑：当前帧权重最高
    const weights = [0.6, 0.3, 0.1]; // 当前、前1帧、前2帧
    const smoothed = this.weightedAverage([current, ...history], weights);

    // 更新历史记录
    history.unshift(current); // 将当前数据添加到开头
    if (history.length > this.historyCacheSize) {
      history.pop(); // 移除最旧的数据
    }
    this.vehicleHistory.set(vehicleId, history);

    return smoothed;
  }

  /**
   * 🎯 加权平均计算方法
   */
  private weightedAverage(
    dataArray: VehicleData[],
    weights: number[]
  ): VehicleData {
    if (dataArray.length === 0) return dataArray[0];

    const result = { ...dataArray[0] }; // 复制第一个数据作为基础

    // 计算加权平均
    let totalWeight = 0;
    let sumLat = 0,
      sumLon = 0,
      sumEle = 0,
      sumHeading = 0,
      sumSpeed = 0;

    for (let i = 0; i < Math.min(dataArray.length, weights.length); i++) {
      const data = dataArray[i];
      const weight = weights[i];

      sumLat += data.latitude * weight;
      sumLon += data.longitude * weight;
      sumEle += (data.elevation || 0) * weight;
      sumHeading += data.heading * weight;
      sumSpeed += (data.speed || 0) * weight;
      totalWeight += weight;
    }

    // 应用平滑后的值
    if (totalWeight > 0) {
      result.latitude = sumLat / totalWeight;
      result.longitude = sumLon / totalWeight;
      result.elevation = sumEle / totalWeight;
      result.heading = sumHeading / totalWeight;
      result.speed = sumSpeed / totalWeight;
    }

    return result;
  }

  public dispose(): void {
    this.stop();
    this.stopProcessingLoop(); // 🚀 停止处理循环
    this.domElement.removeEventListener('click', this.onCanvasClick.bind(this));
    this.instancedMeshes.forEach(mesh => {
      this.scene.remove(mesh);
      mesh.geometry.dispose();
      (Array.isArray(mesh.material) ? mesh.material : [mesh.material]).forEach(
        mat => mat.dispose()
      );
    });
    this.instancedMeshes.clear();
    this.activeAnimations.clear();
    this.vehicleDataMap.clear();
    this.vehicleIdToIndex.clear();
    this.availableIndices.clear();
    this.vehicleHistory.clear(); // 🚀 清理历史数据
    console.log('[DataDrivenVehicleSystem] Disposed.');
  }
}

/**
 * 🏭 创建并初始化一个 DataDrivenVehicleSystem 实例
 * @param scene The THREE.Scene to add vehicles to.
 * @param camera The THREE.Camera for raycasting.
 * @param domElement The canvas element for click events.
 * @param coordinateSystem The V2X coordinate system instance.
 * @param options Configuration options for the system.
 * @returns A promise that resolves to the fully initialized vehicle system.
 */
export async function createDataDrivenVehicleSystem(
  scene: THREE.Scene,
  camera: THREE.Camera,
  domElement: HTMLElement,
  coordinateSystem: CoordinateSystem,
  options: DataDrivenVehicleSystemOptions = {}
): Promise<DataDrivenVehicleSystem> {
  const system = new DataDrivenVehicleSystem(
    scene,
    camera,
    domElement,
    coordinateSystem,
    options
  );
  await system.initialize();
  return system;
}
